import { getAnchorId, getComponentMargin, getComponentOutlineMargin, isRichTextEmpty } from '@/utils/builderUtils';
import beTwMerge from '@/utils/beTwMerge';
import Image from 'next/image';
import useMediaQuery from '@/hooks/useMediaQuery';
import { subHeadersStyles } from '@/consts/builder';
import RichText from '@/components/RichText/RichText';
import { ActionsType } from '@/store/types/IBuilder';
import DropdownButton from '@/components/homepage/DropdownButton';
import Button from '@/components/homepage/Button';
import { useEffect, useRef } from 'react';

interface ThreeImage {
  altText: string;
  href: string;
  src: string;
  textAndButton: {
    actions: ActionsType[];
    copyColor: string;
    description?: string;
    title?: string;
    titleTag: 'h2' | 'h3' | 'h4';
  };
}

type ThreeImageTileProps = {
  anchorLinkID: string;
  description?: string;
  header?: string;
  headerPosition: string;
  headerTag: 'h1' | 'h2';
  image1: ThreeImage;
  image2: ThreeImage;
  image3: ThreeImage;
  isTextUnderImage: boolean;
  margin: boolean;
  outlineMargin: boolean;
  textPosition: string;
};

function Tile({
  buttonRef,
  imageInfo,
  isDesktop,
  isTextUnderImage = false,
  textBoxRef,
  textPosition,
}: {
  buttonRef: any;
  imageInfo: ThreeImage;
  isDesktop: boolean;
  isTextUnderImage?: boolean;
  textBoxRef: any;
  textPosition: string;
}) {
  const { altText, href, src, textAndButton } = imageInfo;
  const renderImage = () => {
    const alt = altText || textAndButton?.title || '';
    const image = <Image alt={alt} className="tw-w-full tw-h-full" height={600} src={src} width={475} />;
    if (href) {
      return (
        <a href={href} className="tw-block tw-w-full tw-object-cover">
          {image}
        </a>
      );
    }
    return image;
  };
  const renderImageText = () => {
    const { actions, copyColor, description, title, titleTag } = textAndButton || {};
    const isTextWhite = copyColor === 'white';
    const TitleTag = titleTag || 'h3';
    const slicedActions: ActionsType[] =
      actions?.slice(0, 1).map(action => ({
        ...action,
        color: (action.color as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
        mobileColor: (action.mobileColor as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
      })) || [];
    let containerClass = 'tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-gap-[24px] tw-px-4';
    if (!isTextUnderImage) {
      containerClass = beTwMerge(
        containerClass,
        'tw-absolute tw-pointer-events-none tw-px-8 tw-py-10 tw-justify-end',
        textPosition === 'top' && 'tw-justify-start',
        textPosition === 'center' && 'tw-justify-center'
      );
    }
    return (
      <div className={containerClass}>
        <div className={beTwMerge('tw-w-full', isTextUnderImage && 'tw-mt-2 large:tw-mt-4')} ref={textBoxRef}>
          {title && (
            <TitleTag
              className={`tw-mb-2 tw-mt-0 ${subHeadersStyles[TitleTag]} tw-text-center ${isTextWhite ? 'tw-text-color-white' : ''}`}
            >
              {title}
            </TitleTag>
          )}
          {description && !isRichTextEmpty(description) && (
            <RichText
              className={`tw-text-center tw-pointer-events-auto ${isTextWhite ? 'tw-text-color-white' : ''}`}
              content={description}
              isOnlyPcTextWhite={false}
              isTextWhite={isTextWhite}
            />
          )}
        </div>
        {slicedActions.length > 0 && (
          <div className="tw-pointer-events-auto tw-flex tw-w-full tw-justify-center">
            {slicedActions.map((action, i) => {
              const { color, isDropdownButton, label, mobileColor, mobileVariant, variant } = action;
              if (isDropdownButton && action.actions?.length) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    btnRef={buttonRef}
                    className="tw-min-w-[146px] tw-font-avenirmedium tw-capitalize tw-w-full tw-text-center [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis"
                    color={isDesktop ? color : mobileColor}
                    key={i}
                    label={label}
                    options={subActions}
                    type={isDesktop ? variant : mobileVariant}
                  />
                );
              }
              return (
                <Button
                  className="tw-min-w-[146px] tw-font-avenirmedium tw-capitalize tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis tw-overflow-hidden"
                  color={isDesktop ? color : mobileColor}
                  href={action.href}
                  key={i}
                  label={label}
                  ref={buttonRef}
                  type={isDesktop ? variant : mobileVariant}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-w-full large:tw-w-2/6">
      <div className="tw-w-full">{renderImage()}</div>
      {renderImageText()}
    </div>
  );
}
export default function ThreeImageTile({
  anchorLinkID,
  description,
  header,
  headerPosition,
  headerTag,
  image1,
  image2,
  image3,
  isTextUnderImage,
  margin,
  outlineMargin,
  textPosition,
}: ThreeImageTileProps) {
  const HeaderTag = headerTag || 'h2';
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const button1Ref = useRef<any>(null);
  const button2Ref = useRef<any>(null);
  const button3Ref = useRef<any>(null);

  const textBoxRef = useRef<any>(null);
  const textBox2Ref = useRef<any>(null);
  const textBox3Ref = useRef<any>(null);

  useEffect(() => {
    const button1Width = button1Ref?.current?.offsetWidth || 0;
    const button2Width = button2Ref?.current?.offsetWidth || 0;
    const button3Width = button3Ref?.current?.offsetWidth || 0;
    let maxWidth = button1Width;
    if (button2Width > maxWidth) {
      maxWidth = button2Width;
    }
    if (button3Width > maxWidth) {
      maxWidth = button3Width;
    }
    if (maxWidth) {
      if (button1Width) {
        button1Ref.current.style.width = `${maxWidth + 1}px`;
      }
      if (button2Width) {
        button2Ref.current.style.width = `${maxWidth + 1}px`;
      }
      if (button3Width) {
        button3Ref.current.style.width = `${maxWidth + 1}px`;
      }
    }
  }, [button1Ref, button2Ref, button3Ref]);

  useEffect(() => {
    if (!isTextUnderImage) return;
    const resizeHeight = () => {
      textBoxRef.current.style.height = 'auto';
      textBox2Ref.current.style.height = 'auto';
      textBox3Ref.current.style.height = 'auto';
      if (window.innerWidth < 1024) return;

      const textBoxHeight = textBoxRef?.current?.offsetHeight || 0;
      const textBox2Height = textBox2Ref?.current?.offsetHeight || 0;
      const textBox3Height = textBox3Ref?.current?.offsetHeight || 0;
      let maxHeight = textBoxHeight;
      if (textBox2Height > maxHeight) {
        maxHeight = textBox2Height;
      }
      if (textBox3Height > maxHeight) {
        maxHeight = textBox3Height;
      }
      if (maxHeight) {
        if (textBoxHeight) {
          textBoxRef.current.style.height = `${maxHeight}px`;
        }
        if (textBox2Height) {
          textBox2Ref.current.style.height = `${maxHeight}px`;
        }
        if (textBox3Height) {
          textBox3Ref.current.style.height = `${maxHeight}px`;
        }
      }
    };
    resizeHeight();
    window.addEventListener('resize', resizeHeight);
    return () => {
      window.removeEventListener('resize', resizeHeight);
    };
  }, [isTextUnderImage, textBoxRef, textBox2Ref, textBox3Ref]);

  return (
    <div
      className={beTwMerge(getComponentOutlineMargin(outlineMargin), getComponentMargin(margin))}
      {...getAnchorId(anchorLinkID)}
    >
      <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-[16px] large:tw-gap-[24px]">
        <div
          className={beTwMerge(
            'tw-w-full tw-text-left tw-pt-4 tw-px-4 large:tw-pt-10 large:tw-px-8',
            headerPosition === 'center' && 'tw-text-center'
          )}
        >
          {header && (
            <HeaderTag
              className={beTwMerge('tw-mb-0', !isRichTextEmpty(description) && 'tw-mb-2', subHeadersStyles[HeaderTag])}
            >
              {header}
            </HeaderTag>
          )}
          {!isRichTextEmpty(description) && <RichText content={description!} />}
        </div>
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-[16px] large:tw-flex-row large:tw-gap-[8px]">
          {image1 && (
            <Tile
              buttonRef={button1Ref}
              imageInfo={image1}
              isDesktop={isDesktop}
              isTextUnderImage={isTextUnderImage}
              textBoxRef={textBoxRef}
              textPosition={textPosition}
            />
          )}
          {image2 && (
            <Tile
              buttonRef={button2Ref}
              imageInfo={image2}
              isDesktop={isDesktop}
              isTextUnderImage={isTextUnderImage}
              textBoxRef={textBox2Ref}
              textPosition={textPosition}
            />
          )}
          {image3 && (
            <Tile
              buttonRef={button3Ref}
              imageInfo={image3}
              isDesktop={isDesktop}
              isTextUnderImage={isTextUnderImage}
              textBoxRef={textBox3Ref}
              textPosition={textPosition}
            />
          )}
        </div>
      </div>
    </div>
  );
}
